import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { appEnv } from '../../../app.config';

export type LoggedMessageChannel = 'chat' | 'email' | 'sms';
export type ChannelFilter = 'All' | LoggedMessageChannel;

export type LoggedMessageStatus =
  | 'queued'
  | 'sent'
  | 'sending'
  | 'failed'
  | 'failed_permanent'
  | 'failed_temporary'
  | 'delivered'
  | 'complained'
  | 'received';

export type LoggedMessageDirection = 'inbound' | 'outbound';

export type ModuleFilter = 'customer' | 'carriers' | 'contractor' | 'other';

export interface LogFilters {
  channel: ChannelFilter;
  status: LoggedMessageStatus | '';
  module: ModuleFilter | '';
  from: string | '';
  to: string | '';
  batchId: string | '';
}

export function defaultLogFilters(): LogFilters {
  return { channel: 'All', status: '', module: '', from: '', to: '', batchId: '' };
}

export interface LoggedMessage {
  id: string;
  threadId: string | null;
  channel: LoggedMessageChannel;
  senderId: string | null;
  senderName: string | null;

  preview: string;
  /** The other party - address/number for email/sms, resolved display name for chat (derived from the thread's two participants). Null for inbound email/sms, where the recipient is always us. */
  recipient: string | null;
  direction: LoggedMessageDirection;
  status: LoggedMessageStatus;
  /** 'sales' is the legacy value from before the Sales CRM -> Customers rename - still readable on older rows. */
  module: 'customer' | 'sales' | 'carriers' | 'contractor' | null;
  /**
   * Email only. Set on a Compose bulk send with more than one recipient -
   * shared by every row that send produced, across their separate
   * per-recipient threads. Lets an admin trace a single row back to (and
   * find every sibling of) the send that created it - see filterByBatch().
   * SMS bulk sends never set this (was already per-recipient with no
   * equivalent merged-thread bug to trace, unlike email's old shared
   * Message-Id issue) - always null for sms/chat rows.
   */
  batchId: string | null;
  sentAt: string | null;
  statusUpdatedAt: string | null;
}

export interface MessageLogPage {
  data: LoggedMessage[];
  nextCursor: string | null;
  totalPages?: number;
}

export interface ChannelPagingState {
  pages: LoggedMessage[][];
  cursors: (string | null)[];
  pageHasNext: boolean[];
  currentPage: number;
  totalPages: number | null;
}

function freshState(): ChannelPagingState {
  return { pages: [], cursors: [null], pageHasNext: [], currentPage: 0, totalPages: null };
}

/** One cache entry per distinct filter combination, not just per channel - a Status/Module/Date filter change fetches its own pages, same as Channel already did. */
function filterKey(filters: LogFilters): string {
  return JSON.stringify(filters);
}

@Injectable({
  providedIn: 'root'
})
export class MessageLogService {
  private stateByFilters = new Map<string, ChannelPagingState>();

  /**
   * Set by Compose right before asking the parent MessagingComponent to
   * switch to the Log tab (see compose.component.ts's viewBatchInLog output
   * and messaging.component.ts's handler) - MessageLogComponent reads and
   * clears this in ngOnInit(). A plain field works because *ngIf mounts a
   * fresh MessageLogComponent every time the Log tab becomes active (tabs
   * aren't router-driven), so ngOnInit reliably fires right after this is set.
   */
  pendingBatchFilter: string | null = null;

  /**
   * Pinged by MessagingComponent's own live chat/CRM thread listeners
   * (already running for the unread-badge count, for the lifetime of the
   * Messages module) on every snapshot - i.e. whenever any thread changes,
   * including the admin's own outbound send. MessageLogComponent listens so
   * the currently-viewed page can silently re-fetch instead of needing a
   * manual Refresh click.
   */
  private readonly changedSubject = new Subject<void>();
  readonly changed$ = this.changedSubject.asObservable();

  notifyChanged(): void {
    this.changedSubject.next();
  }

  constructor(private httpClient: HttpClient) {}

  getState(filters: LogFilters): ChannelPagingState {
    const key = filterKey(filters);
    let state = this.stateByFilters.get(key);
    if (!state) {
      state = freshState();
      this.stateByFilters.set(key, state);
    }
    return state;
  }

  getAllCachedMessages(): LoggedMessage[] {
    return [...this.stateByFilters.values()].flatMap((s) => s.pages.flat());
  }

  invalidate(filters: LogFilters): void {
    this.stateByFilters.set(filterKey(filters), freshState());
  }

  fetchPage(filters: LogFilters, index: number, pageSize: number): Observable<ChannelPagingState> {
    const state = this.getState(filters);

    return this.list(pageSize, state.cursors[index], filters).pipe(
      tap(({ data, nextCursor, totalPages }) => {
        state.pages[index] = data;
        state.pageHasNext[index] = !!nextCursor;
        if (nextCursor) {
          state.cursors[index + 1] = nextCursor;
        }
        if (totalPages !== undefined) {
          state.totalPages = totalPages;
        }
      }),
      map(() => state)
    );
  }

  private list(limit: number, cursor: string | null, filters: LogFilters): Observable<MessageLogPage> {
    const params: Record<string, string> = { limit: String(limit) };
    if (cursor) params['cursor'] = cursor;
    if (filters.channel !== 'All') params['channel'] = filters.channel;
    if (filters.status) params['status'] = filters.status;
    if (filters.module) params['module'] = filters.module;
    if (filters.from) params['from'] = filters.from;
    if (filters.to) params['to'] = filters.to;
    if (filters.batchId) params['batch_id'] = filters.batchId;

    return this.httpClient.get<MessageLogPage>(`${appEnv.api}crm/messaging/log`, { params });
  }
}
